import React from 'react';
import { Helmet } from 'react-helmet';
import Navigation from '@/components/Navigation';
import HeroSection from '@/components/HeroSection';
import ServicesSection from '@/components/ServicesSection';
import AIShowcase from '@/components/AIShowcase';
import ProcessSection from '@/components/ProcessSection';
import FAQSection from '@/components/FAQSection';
import ContactSection from '@/components/ContactSection';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import SafeJsonLd from '@/components/SafeJsonLd';
import { useLanguage } from '@/contexts/LanguageContext';

const Index: React.FC = () => {
  const { language } = useLanguage();
  const isPT = language === 'pt';

  const title = isPT
    ? 'Spawncore — Websites & Automação IA para Pequenas Empresas | Portugal'
    : 'Spawncore — Websites & AI Automation for Small Businesses | Portugal';

  const description = isPT
    ? 'Estúdio digital especializado em websites profissionais e automação com inteligência artificial para pequenas empresas em Portugal. Chatbots, workflows com n8n, SEO e Ads. Marque uma chamada gratuita.'
    : 'Digital studio focused on professional websites and AI automation for small businesses in Portugal. Chatbots, n8n workflows, SEO and Ads. Book a free call.';

  const keywords = isPT
    ? 'automação IA, automação inteligência artificial, agência web Portugal, websites pequenas empresas, chatbots, n8n, automação processos, marketing digital, SEO Portugal, Google Ads, presença online, Azambuja'
    : 'AI automation, artificial intelligence automation, web agency Portugal, websites small business, chatbots, n8n, workflow automation, digital marketing, SEO Portugal, Google Ads, online presence, Azambuja';

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Spawncore',
    alternateName: 'Spawncore Studio',
    url: 'https://spawncore.com',
    logo: 'https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png',
    description: isPT
      ? 'Estúdio digital especializado em websites e automação com inteligência artificial para pequenas empresas em Portugal.'
      : 'Digital studio focused on professional websites and AI automation for small businesses in Portugal.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Largo do Esteiro 6',
      addressLocality: 'Azambuja',
      postalCode: '2050-261',
      addressCountry: 'PT'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'joao@spawncore.com',
      contactType: 'customer service',
      areaServed: 'PT',
      availableLanguage: ['Portuguese', 'English']
    },
    sameAs: [
      'https://www.instagram.com/spawncore.agency/',
      'https://www.facebook.com/profile.php?id=61579331546296'
    ],
    knowsAbout: [
      'Web Design',
      'Web Development',
      'AI Automation',
      'Chatbots',
      'n8n',
      'Workflow Automation',
      'SEO',
      'Google Ads',
      'Social Media',
      'Small Business Marketing'
    ],
    founder: { '@type': 'Person', name: 'João Reis' }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Spawncore',
    url: 'https://spawncore.com',
    inLanguage: isPT ? 'pt-PT' : 'en-US',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://spawncore.com/?s={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  const aiServiceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'AI Automation',
    name: isPT ? 'Automação com Inteligência Artificial' : 'AI Automation',
    description: isPT
      ? 'Chatbots, workflows com n8n, qualificação de leads, atendimento 24/7 e automação de processos com IA para pequenas empresas em Portugal.'
      : 'Chatbots, n8n workflows, lead qualification, 24/7 customer service and AI-driven process automation for small businesses in Portugal.',
    provider: { '@type': 'Organization', name: 'Spawncore', url: 'https://spawncore.com' },
    areaServed: 'PT',
    audience: { '@type': 'BusinessAudience', audienceType: 'Small Business' },
    url: 'https://spawncore.com/services/ai'
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <html lang={isPT ? 'pt' : 'en'} />
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <link rel="canonical" href="https://spawncore.com" />
        <link rel="alternate" hrefLang="pt-PT" href="https://spawncore.com/" />
        <link rel="alternate" hrefLang="en" href="https://spawncore.com/" />
        <link rel="alternate" hrefLang="x-default" href="https://spawncore.com/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spawncore.com" />
        <meta property="og:site_name" content="Spawncore" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png" />
        <meta property="og:locale" content={isPT ? 'pt_PT' : 'en_US'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://spawncore.com" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://spawncore.com/lovable-uploads/c121560e-1be0-4632-8106-cdadaf79cf04.png" />

        {/* SEO directives */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta name="author" content="Spawncore" />
        <meta name="geo.region" content="PT" />
        <meta name="geo.placename" content="Azambuja" />
        <meta name="geo.position" content="39.0693;-8.8662" />
        <meta name="ICBM" content="39.0693, -8.8662" />
      </Helmet>

      <SafeJsonLd data={organizationSchema} />
      <SafeJsonLd data={websiteSchema} />
      <SafeJsonLd data={aiServiceSchema} />

      <header>
        <Navigation />
      </header>
      <main>
        <HeroSection />
        <ServicesSection />
        <AIShowcase />
        <ProcessSection />
        <FAQSection />
        <ContactSection />
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
};

export default Index;
